import { Button } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Input } from '@/components/Input'
import { Loader } from '@/components/Loader'
import { CommonError } from '@/shared/Auth/components/CommonError'
import { PasswordInput } from '@/shared/Auth/components/PasswordInput'
import { Row } from '@/shared/Auth/components/Row'
import { Title } from '@/shared/Auth/components/Title'
import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import styles from './Registration.module.scss'

type RegistrationErrors = {
  _common?: string
  name?: string
  phone?: string
  email?: string
  password?: string
  acceptPolicy?: string
}

type RegistrationFieldName = Exclude<keyof RegistrationErrors, '_common'>

/** Порядок полей формы — по нему ищем первую ошибку для скролла */
const FIELDS_ORDER: RegistrationFieldName[] = [
  'name',
  'phone',
  'email',
  'password',
  'acceptPolicy',
]

interface RegistrationProps {
  className?: string
  isLoading: boolean
  name: string
  phone: string
  email: string
  password: string
  acceptPolicy: boolean
  errors?: RegistrationErrors
  changeName: (value: string, name?: string) => void
  changePhone: (value: string, name?: string) => void
  changeEmail: (value: string, name?: string) => void
  changePassword: (value: string, name?: string) => void
  changeAcceptPolicy: (value: boolean, name?: string) => void
  onRegistration: () => void
}
export const Registration: FC<RegistrationProps> = ({
  className,
  isLoading = false,
  name,
  phone,
  email,
  password,
  acceptPolicy = false,
  errors = {},
  changeName = () => {},
  changePhone = () => {},
  changeEmail = () => {},
  changePassword = () => {},
  changeAcceptPolicy = () => {},
  onRegistration = () => {},
}) => {
  const _changeAcceptPolicy: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      changeAcceptPolicy(event.target.checked)
    },
    []
  )

  const commonErrorRef = useRef<HTMLDivElement>(null)
  const fieldRefs = useRef<
    Record<RegistrationFieldName, HTMLDivElement | null>
  >({
    name: null,
    phone: null,
    email: null,
    password: null,
    acceptPolicy: null,
  })

  const setFieldRef = useCallback(
    (fieldName: RegistrationFieldName) => (element: HTMLDivElement | null) => {
      fieldRefs.current[fieldName] = element
    },
    []
  )

  /**
   * Ошибки выставляются стором внутри onRegistration и попадают в пропс
   * errors только после ререндера, поэтому запоминаем факт сабмита и
   * скроллим к первой ошибке, когда она отрисуется.
   */
  const isScrollToErrorPending = useRef(false)

  const send: FormEventHandler = useCallback(
    (event) => {
      event?.preventDefault()
      isScrollToErrorPending.current = true
      if (onRegistration) {
        onRegistration()
      }
    },
    [onRegistration]
  )

  useEffect(() => {
    if (!isScrollToErrorPending.current) {
      return
    }

    const firstErrorField = FIELDS_ORDER.find((fieldName) => errors[fieldName])
    const errorElement =
      firstErrorField != null
        ? fieldRefs.current[firstErrorField]
        : errors._common
        ? commonErrorRef.current
        : null

    // Ошибок пока нет — форма, например, ушла на сервер. Флаг не сбрасываем:
    // если сервер вернёт ошибки, скроллим к первой из них.
    if (errorElement == null) {
      return
    }

    isScrollToErrorPending.current = false
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errors, commonErrorRef, fieldRefs])
  return (
    <form onSubmit={send} className={className}>
      <Title title="Регистрация" />
      <Row ref={commonErrorRef}>
        <CommonError errors={errors} />
      </Row>
      <Row ref={setFieldRef('name')}>
        <Input
          value={name}
          onChange={changeName}
          name="name"
          error={errors.name}
          placeholder="Имя"
        />
      </Row>
      <Row ref={setFieldRef('phone')}>
        <Input
          value={phone}
          onChange={changePhone}
          name="phone"
          error={errors.phone}
          mask="+{7} (000) 000-00-00"
          placeholder="+7 (999) 999-99-99"
        />
      </Row>
      <Row ref={setFieldRef('email')}>
        <Input
          value={email}
          onChange={changeEmail}
          type="email"
          name="email"
          error={errors.email}
          placeholder="E-mail"
        />
      </Row>
      <Row ref={setFieldRef('password')}>
        <PasswordInput
          value={password}
          onChange={changePassword}
          name="password"
          error={errors.password}
          placeholder="Пароль"
        />
      </Row>
      <Row isAdditional ref={setFieldRef('acceptPolicy')}>
        <div>
          <Checkbox
            checked={acceptPolicy}
            onChange={_changeAcceptPolicy}
            name="acceptPolicy"
            error={errors.acceptPolicy}
          >
            <span className={styles.policyText}>
              Я принимаю{' '}
              <a
                href="/documents/policy"
                className={styles.link}
                target="_blank"
              >
                условия политики обработки персональных данных
              </a>{' '}
              и{' '}
              <a
                href="/documents/user-agreement"
                className={styles.link}
                target="_blank"
              >
                пользовательского соглашения
              </a>
              , а также даю{' '}
              <a
                href="/documents/processing-personal-data"
                className={styles.link}
                target="_blank"
              >
                согласие на обработку персональных данных
              </a>{' '}
              и{' '}
              <a
                href="/documents/agreement-advertising-information-materials"
                className={styles.link}
                target="_blank"
              >
                согласие на информационные рассылки
              </a>
            </span>
          </Checkbox>
        </div>
      </Row>
      <Button className={styles.button} type="submit" disabled={isLoading}>
        Зарегистрироваться
        {isLoading ? <Loader className={styles.loader} onTop /> : null}
      </Button>
    </form>
  )
}
